import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Services
import AdminService from '../../../../services/adminService';
import { SmsService } from '../../../../services/smsService';
import { useSms } from '../../../../hooks/useSms';

// UI Components
import { LoadingSpinner } from '../../../../components/ui';

// Local hooks
import { useSubmissionData, useSalesPlaybook, useAnalytics } from './hooks';

// Local components
import { SubmissionHeader, TabNavigation } from './components';

// Utilities
import {
    normalizeEducationInfo,
    normalizeLanguageInfo,
    normalizeFinancialInfo,
} from './utils/normalizers';

// Tab components (lazy loaded for performance)
const OverviewTab = lazy(() => import('./tabs/OverviewTab'));
const PlaybookTab = lazy(() => import('./tabs/PlaybookTab'));
const AnalyticsTab = lazy(() => import('./tabs/AnalyticsTab'));
const ActivitiesTab = lazy(() => import('./tabs/ActivitiesTab'));
const TimelineTab = lazy(() => import('./tabs/TimelineTab'));
const RisksTab = lazy(() => import('./tabs/RisksTab'));

// Tab loading skeleton
const TabSkeleton = () => (
    <div className="space-y-6">
        <div className="bg-gray-800 rounded-xl p-6 animate-pulse">
            <div className="h-6 bg-gray-700 rounded w-1/4 mb-4" />
            <div className="space-y-3">
                <div className="h-4 bg-gray-700 rounded w-full" />
                <div className="h-4 bg-gray-700 rounded w-3/4" />
                <div className="h-4 bg-gray-700 rounded w-1/2" />
            </div>
        </div>
    </div>
);

/**
 * SubmissionDetail - Modular Orchestrator Component
 * 
 * This is the refactored version of SubmissionDetailPage that uses
 * extracted hooks and tab components for better maintainability.
 */
const SubmissionDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Use extracted hooks for data management
    const {
        submission,
        loading,
        error,
        scoringResults,
        loadingScores,
        leadActivities,
        communicationHistory,
        conversionTimeline,
        reload,
        setSubmission,
        setConversionTimeline,
    } = useSubmissionData(id);

    const {
        salesPlaybook,
        tamilPlaybook,
        playbookLanguage,
        loadingPlaybook,
        generatingPlaybook,
        translatingPlaybook,
        reanalyzingCV,
        setPlaybookLanguage,
        loadPlaybook,
        forceReanalyzeCv,
        translateToTamil: translatePlaybookToTamil,
        generatePlaybook,
    } = useSalesPlaybook(submission, scoringResults);

    const {
        aiAnalytics,
        tamilAnalytics,
        analyticsLanguage,
        loadingAdvancedAnalytics,
        generatingAnalytics,
        translatingAnalytics,
        salesMetrics,
        riskFactors,
        opportunityScore,
        followUpTasks,
        setAnalyticsLanguage,
        loadTamilAnalytics,
        translateToTamil: translateAnalyticsToTamil,
        generateAnalytics,
        generateTimeline,
        loadingTimeline,
    } = useAnalytics(submission);

    // Local UI state
    const [activeTab, setActiveTab] = useState('overview');
    const [showAddActivity, setShowAddActivity] = useState(false);
    const [newActivity, setNewActivity] = useState({
        type: '',
        title: '',
        description: '',
        outcome: '',
        nextAction: '',
        nextActionDate: '',
        priority: 'medium',
        status: 'pending'
    });

    // SMS functionality
    const { sendSms, sendWelcomeSms, loading: smsLoading } = useSms();

    // Load playbook and analytics when submission changes
    useEffect(() => {
        if (submission?.cv_data) {
            loadPlaybook(submission);
            loadTamilAnalytics(submission);
        }
    }, [submission, loadPlaybook, loadTamilAnalytics]);

    // Status update handler
    const updateSubmissionStatus = async (newStatus) => {
        try {
            await AdminService.updateWizardSubmissionStatus(id, newStatus);
            setSubmission(prev => ({ ...prev, status: newStatus }));
        } catch (err) {
            console.error('Error updating status:', err);
        }
    };

    // Activity handler
    const handleAddActivity = async () => {
        if (!newActivity.type || !newActivity.title) return;

        try {
            await AdminService.addLeadActivity(id, {
                activity_type: newActivity.type,
                title: newActivity.title,
                description: newActivity.description,
                outcome: newActivity.outcome,
                next_action: newActivity.nextAction,
                next_action_date: newActivity.nextActionDate,
                priority: newActivity.priority,
                status: newActivity.status,
            });
            setShowAddActivity(false);
            setNewActivity({
                type: '',
                title: '',
                description: '',
                outcome: '',
                nextAction: '',
                nextActionDate: '',
                priority: 'medium',
                status: 'pending'
            });
            reload(); // Refresh data
        } catch (err) {
            console.error('Error adding activity:', err);
        }
    };

    // Follow-up task completion handler
    const handleCompleteTask = async (taskId) => {
        // Implementation would go here
        console.log('Complete task:', taskId);
    };

    // SMS handlers
    const handleSendWelcomeSms = async () => {
        if (!submission?.phone) {
            alert('No phone number available');
            return;
        }
        if (!SmsService.isValidSriLankanPhone(submission.phone)) {
            alert('Please enter a valid Sri Lankan mobile number');
            return;
        }
        try {
            await sendWelcomeSms(submission);
        } catch (err) {
            console.error('Failed to send SMS:', err);
        }
    };

    // Handle timeline generation with state update
    const handleGenerateTimeline = async () => {
        const timeline = await generateTimeline?.(submission);
        if (timeline) {
            setConversionTimeline(timeline);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
                <LoadingSpinner size="xlarge" message="Loading submission details..." />
            </div>
        );
    }

    // Error state
    if (error || !submission) {
        return (
            <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
                <div className="admin-card max-w-md w-full mx-auto text-center">
                    <div className="text-red-500 text-4xl mb-4">❌</div>
                    <h2 className="text-2xl font-bold mb-2">Something went wrong</h2>
                    <p className="text-gray-400 mb-4">{error || 'Submission not found'}</p>
                    <button
                        onClick={() => navigate('/admin')}
                        className="admin-btn admin-btn-primary"
                    >
                        ← Back to Admin Dashboard
                    </button>
                </div>
            </div>
        );
    }

    // Normalize data for display
    const educationInfo = normalizeEducationInfo(submission.education_info);
    const languageInfo = normalizeLanguageInfo(submission.language_info);
    const financialInfo = normalizeFinancialInfo(submission.financial_info);

    return (
        <div className="min-h-screen bg-gray-900 font-mono text-white">
            {/* Header */}
            <div className="p-6 border-b border-gray-800">
                <SubmissionHeader
                    submission={submission}
                    opportunityScore={opportunityScore}
                    onContactClick={() => window.location.href = `tel:${submission.phone}`}
                    onAddActivityClick={() => setShowAddActivity(true)}
                    onSmsClick={handleSendWelcomeSms}
                    onStatusChange={updateSubmissionStatus}
                    smsLoading={smsLoading}
                />
            </div>

            {/* Tab Navigation */}
            <TabNavigation
                activeTab={activeTab}
                onTabChange={setActiveTab}
                className="mb-6"
            />

            {/* Tab Content */}
            <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <Suspense fallback={<TabSkeleton />}>
                    {activeTab === 'overview' && (
                        <OverviewTab
                            submission={submission}
                            educationInfo={educationInfo}
                            languageInfo={languageInfo}
                            financialInfo={financialInfo}
                            opportunityScore={opportunityScore}
                            salesMetrics={salesMetrics}
                            followUpTasks={followUpTasks}
                            onCompleteTask={handleCompleteTask}
                        />
                    )}

                    {activeTab === 'playbook' && (
                        <PlaybookTab
                            submission={submission}
                            salesPlaybook={salesPlaybook}
                            tamilPlaybook={tamilPlaybook}
                            playbookLanguage={playbookLanguage}
                            loadingPlaybook={loadingPlaybook}
                            generatingPlaybook={generatingPlaybook}
                            translatingPlaybook={translatingPlaybook}
                            reanalyzingCV={reanalyzingCV}
                            onLanguageChange={setPlaybookLanguage}
                            onTranslateToTamil={translatePlaybookToTamil}
                            onReanalyzeCv={forceReanalyzeCv}
                            onRegeneratePlaybook={() => generatePlaybook?.(submission, true)}
                            onGeneratePlaybook={() => generatePlaybook?.(submission)}
                        />
                    )}

                    {activeTab === 'analytics' && (
                        <AnalyticsTab
                            aiAnalytics={aiAnalytics}
                            tamilAnalytics={tamilAnalytics}
                            analyticsLanguage={analyticsLanguage}
                            salesMetrics={salesMetrics}
                            riskFactors={riskFactors}
                            generatingAnalytics={generatingAnalytics}
                            translatingAnalytics={translatingAnalytics}
                            onGenerateAnalytics={() => generateAnalytics?.(submission)}
                            onLanguageChange={setAnalyticsLanguage}
                            onTranslateToTamil={translateAnalyticsToTamil}
                        />
                    )}

                    {activeTab === 'activities' && (
                        <ActivitiesTab
                            leadActivities={leadActivities}
                            communicationHistory={communicationHistory}
                            onAddActivity={() => setShowAddActivity(true)}
                            onAddCommunication={() => alert('Add Communication feature coming soon!')}
                        />
                    )}

                    {activeTab === 'timeline' && (
                        <TimelineTab
                            conversionTimeline={conversionTimeline}
                            loadingTimeline={loadingTimeline}
                            onGenerateTimeline={handleGenerateTimeline}
                        />
                    )}

                    {activeTab === 'risks' && (
                        <RisksTab riskFactors={riskFactors} />
                    )}
                </Suspense>
            </main>

            {/* Add Activity Modal */}
            {showAddActivity && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
                            <h3 className="text-xl font-bold text-white">Add New Activity</h3>
                            <button
                                onClick={() => {
                                    setShowAddActivity(false);
                                    setNewActivity({ type: '', title: '', description: '', outcome: '', nextAction: '', nextActionDate: '', priority: 'medium', status: 'pending' });
                                }}
                                className="text-gray-400 hover:text-white"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            {/* Activity Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Activity Type *</label>
                                <select
                                    value={newActivity.type}
                                    onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                >
                                    <option value="">Select type...</option>
                                    <option value="call">Phone Call</option>
                                    <option value="email">Email</option>
                                    <option value="meeting">Meeting</option>
                                    <option value="follow_up">Follow Up</option>
                                    <option value="note">Note</option>
                                    <option value="document_sent">Document Sent</option>
                                    <option value="quote_sent">Quote Sent</option>
                                </select>
                            </div>

                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
                                <input
                                    type="text"
                                    value={newActivity.title}
                                    onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                                    placeholder="Brief title for this activity"
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                                <textarea
                                    value={newActivity.description}
                                    onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                                    placeholder="Detailed notes about this activity..."
                                    rows={4}
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Outcome and Priority Row */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Outcome</label>
                                    <select
                                        value={newActivity.outcome}
                                        onChange={(e) => setNewActivity({ ...newActivity, outcome: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Select outcome...</option>
                                        <option value="positive">Positive</option>
                                        <option value="neutral">Neutral</option>
                                        <option value="negative">Negative</option>
                                        <option value="no_response">No Response</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                                    <select
                                        value={newActivity.priority}
                                        onChange={(e) => setNewActivity({ ...newActivity, priority: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                    </select>
                                </div>
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                                <select
                                    value={newActivity.status}
                                    onChange={(e) => setNewActivity({ ...newActivity, status: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="pending">Pending</option>
                                    <option value="completed">Completed</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>

                            {/* Next Action */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Next Action</label>
                                <input
                                    type="text"
                                    value={newActivity.nextAction}
                                    onChange={(e) => setNewActivity({ ...newActivity, nextAction: e.target.value })}
                                    placeholder="What needs to be done next?"
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Next Action Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Next Action Date</label>
                                <input
                                    type="datetime-local"
                                    value={newActivity.nextActionDate}
                                    onChange={(e) => setNewActivity({ ...newActivity, nextActionDate: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-gray-700/50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-700">
                            <button
                                onClick={() => {
                                    setShowAddActivity(false);
                                    setNewActivity({ type: '', title: '', description: '', outcome: '', nextAction: '', nextActionDate: '', priority: 'medium', status: 'pending' });
                                }}
                                className="px-4 py-2 text-gray-300 bg-gray-800 border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddActivity}
                                disabled={!newActivity.type || !newActivity.title}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Add Activity
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubmissionDetail;
